import { useEffect, useCallback, useRef } from "react"
import { useActivityStore, ActivityItem } from "@stores/activityStore"


const useActivity = () => {
  // Get store actions and state
  const { 
    fetchActivities: storeFetchActivities, 
    cache, 
    isReachingEnd, 
    error, 
    isLoading, 
    isLoadingMore,
    currentPage,
    setCurrentPage 
  } = useActivityStore()

  // We're using the persisted currentPage from the store instead of local state
  
  // Function to get all activities from cache
  const getAllActivities = useCallback(() => {
    const allActivities: ActivityItem[] = []
    
    // Get all keys from the cache and sort them numerically
    const pageKeys = Object.keys(cache)
      .filter(key => !isNaN(Number(key))) // Filter numeric keys only
      .map(Number)
      .sort((a, b) => a - b)
    
    // Collect all activities from cache in order by page
    for (const pageKey of pageKeys) {
      if (pageKey <= currentPage && cache[pageKey]?.data) {
        allActivities.push(...cache[pageKey].data)
      }
    }
    
    return allActivities
  }, [cache, currentPage])

  // Always fetch on component mount, but don't block UI rendering if we have cache
  useEffect(() => {
    // Always fetch fresh data in background regardless of cache status
    // This gives us the show-cache-then-update behavior
    storeFetchActivities(1, false, true).then(success => {
      if (success) {
        // If we have a stored currentPage > 1, fetch additional pages
        // This ensures we restore the user's previous view after logout/login
        const initialPages = Math.min(currentPage, 3); // Load at most 3 pages automatically
        
        // Load pages 2 through initialPages in sequence
        const loadInitialPages = async () => {
          for (let page = 2; page <= initialPages; page++) {
            // Always refresh all cached pages
            await storeFetchActivities(page, true, true);
          }
        };
        
        if (initialPages > 1) {
          loadInitialPages();
        }
      }
    });
  }, []); // Empty dependency array - only run once on mount

  // Use a ref to track if we're currently loading more data (this updates synchronously)
  const loadingMoreRef = useRef(false);
  
  // Update ref whenever the store's isLoadingMore changes
  useEffect(() => {
    loadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Function to load more data with protection against multiple calls
  const loadMore = useCallback(async () => {
    // Check if we've reached the end or already loading
    if (isReachingEnd || loadingMoreRef.current) return;
    
    // Immediately mark as loading to prevent concurrent calls
    loadingMoreRef.current = true;
    
    // Calculate next page but don't update state yet
    const nextPage = currentPage + 1;
    
    // Fetch data first and only update currentPage if successful
    const success = await storeFetchActivities(nextPage, true);
    if (success) {
      setCurrentPage(nextPage);
    }
  }, [currentPage, isReachingEnd, storeFetchActivities, setCurrentPage])


  // Function to refetch data
  const refetch = useCallback(async () => {
    // No need to invalidate cache - we'll just fetch fresh data
    return storeFetchActivities(1, false, true)
  }, [storeFetchActivities])

  return {
    data: getAllActivities(),
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    isReachingEnd,
    refetch
  }
}

export default useActivity